import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaWhatsapp, FaInstagram } from "react-icons/fa";
import { HiArrowRight } from "react-icons/hi";
import MetaTag from "../../components/MetaTag/MetaTag";
import SiteHeader, { useAnchor } from "../../site/SiteHeader";
import SiteFooter from "../../site/SiteFooter";
import { Eyebrow, Reveal } from "../../site/Brand";
import PropertyCard from "../../components/Property/PropertyCard";
import { OFFICES, igLink, waLink } from "../../site/contacts";
import { SliderData as REVIEWS } from "../../site/reviews";
import { publicApi } from "../../api/client";
import "../../site/site.css";

import dubaiSkyline from "../../assets/photos/dubai-skyline.webp";
import istanbulGalata from "../../assets/photos/istanbul-galata.webp";
import terraceSea from "../../assets/photos/terrace-sea.webp";
import interior from "../../assets/photos/interior-living.webp";

const pad = (n) => String(n).padStart(2, "0");

// Слайды первого экрана: консультанты обоих направлений по очереди
const HERO_SLIDES = [
  { office: OFFICES.dubai, chip: "site.hero.chip" },
  { office: OFFICES.istanbul, chip: "site.hero.chip2" },
];

function Hero() {
  const { t } = useTranslation();
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (paused || reduce) return undefined;
    const id = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 6000);
    return () => clearInterval(id);
  }, [paused]);

  return (
    <section className="hero">
      <div className="wrap">
        <div className="hero__grid">
          <Reveal className="hero__text">
            <Eyebrow line>{t("site.hero.eyebrow")}</Eyebrow>
            <h1 className="display">
              {t("site.hero.title1")}
              <br />
              <span className="muted-line">{t("site.hero.title2")}</span>
            </h1>
            <p className="lead">{t("site.hero.text")}</p>
            <div className="hero__actions">
              <Link to="/properties" className="btn-pill">
                {t("site.hero.cta")} <HiArrowRight />
              </Link>
              {/* WhatsApp того консультанта, который сейчас на фото */}
              <a
                href={waLink(HERO_SLIDES[slide].office)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-pill btn-pill--ghost"
                key={`wa-${slide}`}
              >
                <FaWhatsapp /> {t(`site.hero.whatsappTo.${HERO_SLIDES[slide].office.key}`)}
              </a>
            </div>
            <p className="hero__since">{t("site.hero.since")}</p>
          </Reveal>
          <Reveal className="hero__media" delay={150}>
            <div className="arch" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
              {HERO_SLIDES.map((s, i) => (
                <img
                  key={s.chip}
                  src={s.office.photo}
                  alt={t(`site.team.${s.office.key}.name`)}
                  className={i === slide ? "is-active" : ""}
                  aria-hidden={i !== slide}
                />
              ))}
            </div>
            <span className="chip chip--light hero__chip" key={slide}>
              {t(HERO_SLIDES[slide].chip)}
            </span>
            <div className="hero__dots">
              {HERO_SLIDES.map((s, i) => (
                <button
                  key={s.chip}
                  type="button"
                  className={i === slide ? "is-active" : ""}
                  onClick={() => setSlide(i)}
                  aria-label={t(s.chip)}
                />
              ))}
            </div>
          </Reveal>
        </div>
      </div>
      <div className="hero__base" />
    </section>
  );
}

function Quick() {
  const { t } = useTranslation();
  const items = t("site.quick", { returnObjects: true });
  const links = ["/properties?country=uae", "/properties?country=turkey", "/permanent-residency/ikamet"];
  return (
    <div className="wrap">
      <Reveal className="quick">
        {items.map((it, i) => (
          <Link to={links[i]} key={it.title}>
            <span className="quick__num">{pad(i + 1)}</span>
            <span>
              <span className="quick__label">{it.label}</span>
              <span className="quick__title">{it.title}</span>
            </span>
            <HiArrowRight />
          </Link>
        ))}
      </Reveal>
    </div>
  );
}

function About() {
  const { t } = useTranslation();
  const anchor = useAnchor();
  const stats = t("site.stats", { returnObjects: true });
  return (
    <section className="section" id="about">
      <div className="wrap">
        <div className="split">
          <Reveal>
            <Eyebrow>{t("site.about.eyebrow")}</Eyebrow>
            <h2 className="h2">
              {t("site.about.title1")}
              <br />
              <span className="muted-line">{t("site.about.title2")}</span>
            </h2>
          </Reveal>
          <Reveal className="split__side" delay={120}>
            <p className="lead">{t("site.about.text")}</p>
            <a href="#contacts" className="link-line" onClick={anchor("contacts")}>
              {t("site.about.link")} <HiArrowRight />
            </a>
          </Reveal>
        </div>
        <Reveal className="stats" delay={100} style={{ marginTop: 72 }}>
          {stats.map((s) => (
            <div key={s.value}>
              <strong>{s.value}</strong>
              <span>{s.label}</span>
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

function Directions() {
  const { t } = useTranslation();
  const cards = [
    { key: "dubai", img: dubaiSkyline, to: "/properties?country=uae" },
    { key: "istanbul", img: istanbulGalata, to: "/properties?country=turkey" },
  ];
  return (
    <section className="section section--sand">
      <div className="wrap">
        <Reveal className="section-head">
          <div>
            <Eyebrow>{t("site.directions.eyebrow")}</Eyebrow>
            <h2 className="h2">{t("site.directions.title")}</h2>
          </div>
        </Reveal>
        <div className="directions">
          {cards.map((c, i) => (
            <Reveal key={c.key} delay={i * 120}>
              <Link to={c.to} className="direction">
                <img src={c.img} alt={t(`site.directions.${c.key}.title`)} loading="lazy" />
                <span className="chip">{t(`site.directions.${c.key}.tag`)}</span>
                <h3 className="direction__title">{t(`site.directions.${c.key}.title`)}</h3>
                <p>{t(`site.directions.${c.key}.text`)}</p>
                <span className="link-line">
                  {t(`site.directions.${c.key}.link`)} <HiArrowRight />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Featured() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  useEffect(() => {
    publicApi
      .properties({ limit: 4, recommended: true })
      .then((d) => setItems(d.items))
      .catch(() => setItems([]));
  }, []);
  if (!items.length) return null;
  return (
    <section className="section">
      <div className="wrap">
        <Reveal className="section-head">
          <div>
            <Eyebrow>{t("site.featured.eyebrow")}</Eyebrow>
            <h2 className="h2">{t("site.featured.title")}</h2>
          </div>
          <Link to="/properties" className="btn-pill btn-pill--ghost">
            {t("site.featured.all")} <HiArrowRight />
          </Link>
        </Reveal>
        <div className="cards">
          {items.map((p, i) => (
            <Reveal key={p.id} delay={i * 80} style={i === 0 ? { gridColumn: "1 / -1" } : undefined}>
              <PropertyCard property={p} wide={i === 0} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Services() {
  const { t } = useTranslation();
  const items = t("site.services.items", { returnObjects: true });
  return (
    <section className="section">
      <div className="wrap">
        <div className="split">
          <Reveal>
            <Eyebrow>{t("site.services.eyebrow")}</Eyebrow>
            <h2 className="h2">{t("site.services.title")}</h2>
            <p className="lead" style={{ marginTop: 24 }}>
              {t("site.services.text")}
            </p>
          </Reveal>
          <Reveal className="services" delay={120}>
            {items.map((s, i) => (
              <div className="service" key={s.title}>
                <span className="service__num">{pad(i + 1)}</span>
                <div>
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                </div>
              </div>
            ))}
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Mood() {
  const { t, i18n } = useTranslation();
  const en = i18n.language === "en";
  return (
    <section className="section section--tight">
      <div className="wrap">
        <div className="mood">
          <Reveal as="figure">
            <img src={terraceSea} alt="" loading="lazy" />
            <span className="chip">{en ? "Life by the sea" : "Жизнь у моря"}</span>
            <figcaption>{en ? "A home you want to come back to" : "Дом, в который хочется возвращаться"}</figcaption>
          </Reveal>
          <Reveal as="figure" delay={120}>
            <img src={interior} alt="" loading="lazy" />
            <span className="chip">{t("site.footer.tagline")}</span>
            <figcaption>{en ? "Space that works for you" : "Пространство под вашу жизнь"}</figcaption>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function TeamSection() {
  const { t } = useTranslation();
  const people = [
    ["dubai", OFFICES.dubai],
    ["istanbul", OFFICES.istanbul],
  ];
  return (
    <section className="section section--sand" id="team">
      <div className="wrap">
        <Reveal className="section-head">
          <div>
            <Eyebrow>{t("site.team.eyebrow")}</Eyebrow>
            <h2 className="h2">{t("site.team.title")}</h2>
          </div>
          <p className="lead">{t("site.team.text")}</p>
        </Reveal>
        <div className="team">
          {people.map(([city, o], i) => (
            <Reveal className="agent" key={city} delay={i * 120}>
              <div className="agent__photo">
                {o.photo ? (
                  <img src={o.photo} alt={t(`site.team.${o.key}.name`)} loading="lazy" />
                ) : (
                  <div className="agent__placeholder">{o.initials}</div>
                )}
              </div>
              <div>
                <span className="agent__city">{t(`site.footer.${city}`)}</span>
                <h3>{t(`site.team.${o.key}.name`)}</h3>
                <p className="agent__role">{t(`site.team.${o.key}.role`)}</p>
                <p className="agent__bio">{t(`site.team.${o.key}.bio`)}</p>
                <div className="agent__links">
                  <a className="btn-pill btn-pill--small" href={waLink(o)} target="_blank" rel="noopener noreferrer">
                    <FaWhatsapp /> {t("site.team.whatsapp")}
                  </a>
                  <a className="btn-pill btn-pill--small btn-pill--ghost" href={igLink(o)} target="_blank" rel="noopener noreferrer">
                    <FaInstagram /> @{o.instagram}
                  </a>
                </div>
                <a className="agent__phone" href={`tel:+${o.whatsapp}`}>
                  {o.phone}
                </a>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Reviews() {
  const { t } = useTranslation();
  const list = REVIEWS.filter((r) => r.description).slice(0, 6);
  if (!list.length) return null;
  return (
    <section className="section">
      <div className="wrap">
        <Reveal className="section-head">
          <div>
            <Eyebrow>{t("site.reviews.eyebrow")}</Eyebrow>
            <h2 className="h2">{t("site.reviews.title")}</h2>
          </div>
          <span className="small">{t("site.reviews.note")}</span>
        </Reveal>
        <div className="reviews">
          {list.map((r, i) => (
            <Reveal className="review" key={r.name + i} delay={(i % 3) * 100}>
              <span className="review__quote">“</span>
              <p>{r.description}</p>
              <span className="review__name">{r.name}</span>
              <span className="review__src">Instagram</span>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Process() {
  const { t } = useTranslation();
  const steps = t("site.process.steps", { returnObjects: true });
  return (
    <section className="section section--tight" style={{ paddingBottom: 120 }}>
      <div className="wrap">
        <Reveal className="section-head">
          <div>
            <Eyebrow>{t("site.process.eyebrow")}</Eyebrow>
            <h2 className="h2">{t("site.process.title")}</h2>
          </div>
        </Reveal>
        <Reveal className="steps">
          {steps.map((s, i) => (
            <div className="step" key={s.title}>
              <span className="step__num">{pad(i + 1)}</span>
              <h3>{s.title}</h3>
              <p>{s.text}</p>
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

const Home = () => {
  const { t } = useTranslation();
  return (
    <>
      <MetaTag title={t("seo.home.title")} description={t("seo.home.description")} />
      <SiteHeader />
      <main>
        <Hero />
        <Quick />
        <About />
        <Directions />
        <Featured />
        <Services />
        <Mood />
        <TeamSection />
        <Reviews />
        <Process />
      </main>
      <SiteFooter />
    </>
  );
};

export default Home;
